import { ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit } from "@angular/core";
import { NgClass, NgFor, NgIf } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { SubscriptionLike, finalize } from "rxjs";
import { Area } from "src/app/shared/model/tickets/area.model";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { TicketsService } from "src/app/tickets/tickets.service";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { GenericoService } from "src/app/shared/services/generico.service";
import { OrganigramaComponent } from "../organigrama/organigrama.component";

type AreaListState = {
  currentPage?: number;
  pageSize?: number;
};

@Component({
  selector: "app-gestion-areas",
  standalone: true,
  templateUrl: "./gestion-areas.component.html",
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    NgIf,
    NgFor,
    MatSlideToggleModule,
    NgClass,
    OrganigramaComponent,
  ],
})
export class GestionAreasComponent implements OnInit, OnDestroy {
  /**
   * La pestaña abierta.
   *
   * El organigrama se monta hasta que se entra a él y no de arranque:
   * trae cuarenta y tantos nodos y todas las personas, y quien sólo
   * viene a dar de alta un área no tiene por qué esperar eso.
   */
  pestana: "areas" | "organigrama" = "areas";

  listaAreas: Area[] = [];
  consultaObject = new ConsultaList<Area>();
  totalRegistros = 0;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];
  currentPage = 1;
  loading = false;

  @HostBinding("class.is-open")
  isOpen = false;

  regresaPag = false;
  private subscription: SubscriptionLike | null = null;

  constructor(
    private router: Router,
    private ticketService: TicketsService,
    private msg: Msg,
    private generico: GenericoService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscription = this.generico.isOpen.subscribe((res) => {
      this.regresaPag = res;
      this.cdr.detectChanges();
    });

    this.restoreListState();
    queueMicrotask(() => {
      this.consultaPagina(this.currentPage);
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.generico.getEstatusPantalla(false);
  }

  consultaPagina(page: number): void {
    this.currentPage = page;
    this.loading = this.listaAreas.length === 0;
    this.cdr.detectChanges();

    // Solo las activas: las dadas de baja siguen en la base para que el
    // historico de tickets y proyectos se pueda leer, pero no tienen por
    // que estorbar en el catalogo.
    this.consultaObject.param1 = "ACTIVO";
    this.consultaObject.regInicial = (page - 1) * this.pageSize;
    this.consultaObject.regFinal = this.pageSize;
    this.consultaObject.regXPagina = this.pageSize;

    this.ticketService
      .consultarAreaPaginacion(this.consultaObject)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe((res) => {
        this.listaAreas = Array.isArray(res?.list) ? [...res.list] : [];
        this.totalRegistros = Number(res?.param4 ?? res?.pTotalItems ?? this.listaAreas.length ?? 0);

        const maxPages = this.totalPages;
        if (maxPages > 0 && this.currentPage > maxPages) {
          this.consultaPagina(maxPages);
          return;
        }

        this.cdr.detectChanges();
      });
  }

  altaModificarArea(objectArea: Area | null): void {
    const listState = this.buildListState();

    if (objectArea == null) {
      this.router.navigate(["/admin/areas/alta"], {
        state: { listState },
      });
      return;
    }

    this.router.navigate(["/admin/areas/edicion", objectArea.id], {
      state: {
        area: this.cloneArea(objectArea),
        listState,
      },
    });
  }

  onClicAcualizaStatus(area: Area, evt: any): void {
    if (evt.checked) {
      this.msg.questionCall(
        "¿Deseas cambiar el área por default para atención de tickets?",
        null,
        (res) => {
          if (res.value) {
            this.listaAreas.forEach((item) => (item.defaultService = false));
            area.defaultService = true;
            this.actualizaEstatusBD(area);
          } else {
            area.defaultService = false;
            evt.source.checked = false;
            this.cdr.detectChanges();
          }
        },
      );
      return;
    }

    this.msg.questionCall(
      "¿Deseas quitar esta área como defaul para atención tickets?",
      null,
      (res) => {
        if (res.value) {
          area.defaultService = false;
          this.actualizaEstatusBD(area);
        } else {
          area.defaultService = true;
          evt.source.checked = true;
          this.cdr.detectChanges();
        }
      },
    );
  }

  actualizaEstatusBD(area: Area): void {
    this.ticketService.guardarArea(area).subscribe((res) => {
      if (+res.pCodRetorno === 0) {
        this.msg.successCall(
          "El área de atención de ticket por default se cambió con éxito",
          null,
          null,
        );
        this.consultaPagina(this.currentPage);
      } else {
        this.msg.warningCall(
          "No se puede cambiar el área de atención de tickets por default",
          null,
          null,
        );
      }
    });
  }

  /** Boton "Regresar": lo activa Administracion de proyectos
   *  (modal-agregar-proyecto, getEstatusPantalla(true)) al mandar aqui a dar de alta un area. */
  regresaProyectos(): void {
    this.generico.getEstatusPantalla(false);
    this.router.navigate(["ODP/admproyectos"]);
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.consultaPagina(1);
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }
    this.consultaPagina(page);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  get totalPages(): number {
    return this.totalRegistros > 0 ? Math.ceil(this.totalRegistros / this.pageSize) : 0;
  }

  get showPagination(): boolean {
    return this.totalRegistros > 0 || this.listaAreas.length > 0;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  getStatusClass(area: Area): string {
    return area?.estatus === "ACTIVO" ? "is-active" : "is-inactive";
  }

  getTypeLabel(area: Area): string {
    return area?.tipo?.toString() || "Sin tipo";
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);

    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === "number";
  }

  private restoreListState(): void {
    const listState = history.state?.listState as AreaListState | undefined;
    if (!listState) {
      return;
    }

    const restoredPage = Number(listState.currentPage);
    if (Number.isFinite(restoredPage) && restoredPage > 0) {
      this.currentPage = restoredPage;
    }

    const restoredPageSize = Number(listState.pageSize);
    if (
      Number.isFinite(restoredPageSize) &&
      this.pageSizeOptions.includes(restoredPageSize)
    ) {
      this.pageSize = restoredPageSize;
    }
  }

  private buildListState(): AreaListState {
    return {
      currentPage: this.currentPage,
      pageSize: this.pageSize,
    };
  }

  private cloneArea(area: Area): Area {
    return JSON.parse(JSON.stringify(area)) as Area;
  }
}
